import type { LocalIagoEvent } from 'common/models/iago-event.interface';
import type {
  IagoPortEvent,
  IagoPortRequest,
  IagoPortRequests,
  WireLocalIagoEvent,
} from 'common/models/iago-port-protocol';

import {
  hasIagoPortCapability,
  resetAnnouncedIagoPortCapabilities,
  setAnnouncedIagoPortCapabilities,
} from './iago-port-capabilities';
import { ipcToClient, sendIpc } from './send-ipc';

type IagoPortWindowMessage = {
  __iagoPort?: boolean;
  epoch?: number;
};

let didInit = false;
let port: MessagePort | undefined;
let currentEpoch = 0;
let isReady = false;
let didLogDroppedIngest = false;
let didLogDroppedInvalidate = false;

type IagoPortWindow = Window & {
  globals?: {
    appStore?: {
      dispatch?: (action: ReturnType<typeof ipcToClient>) => void;
    };
  };
};

function isIagoPortWindowMessage(data: unknown): data is IagoPortWindowMessage {
  return !!data && typeof data === 'object' && (data as IagoPortWindowMessage).__iagoPort === true;
}

function dispatchIagoEvent(event: LocalIagoEvent) {
  (window as IagoPortWindow).globals?.appStore?.dispatch?.(
    ipcToClient({ type: 'onIagoEvent', handlerArgs: [event] }),
  );
}

function cursorDataToNumberArray(data: Uint8Array | number[]) {
  return Array.from(data);
}

function localIagoEventFromWire(event: WireLocalIagoEvent): LocalIagoEvent {
  if (event.type !== 'cursor-change') return event as LocalIagoEvent;

  if (event.cursorType === 'single-dpi-aware-representation') {
    return {
      ...event,
      singleDpiAwareRepresentation: event.singleDpiAwareRepresentation && {
        ...event.singleDpiAwareRepresentation,
        data: cursorDataToNumberArray(event.singleDpiAwareRepresentation.data),
      },
    } as LocalIagoEvent;
  }

  return {
    ...event,
    multipleRepresentations: event.multipleRepresentations?.map((representation) => ({
      ...representation,
      data: cursorDataToNumberArray(representation.data),
    })),
  } as LocalIagoEvent;
}

function resetControlViewToHost() {
  dispatchIagoEvent({
    type: 'control-change',
    peerId: 'self',
    role: 'local',
  });
}

function handlePortEvent(event: IagoPortEvent) {
  switch (event.type) {
    case 'ready':
      setAnnouncedIagoPortCapabilities(event.capabilities);
      isReady = true;
      didLogDroppedIngest = false;
      didLogDroppedInvalidate = false;
      return;
    case 'listener-started':
      didLogDroppedIngest = false;
      didLogDroppedInvalidate = false;
      return;
    case 'iago-event':
      dispatchIagoEvent(localIagoEventFromWire(event.event));
      return;
  }
}

function closePort(portToClose: MessagePort) {
  try {
    portToClose.close();
  } catch (error) {
    console.error('[iago-port] failed to close MessagePort', error);
  }
}

function adoptPort(nextPort: MessagePort, epoch: number) {
  const previousPort = port;
  const isReconnect = !!previousPort;

  port = nextPort;
  currentEpoch = epoch;
  isReady = false;
  resetAnnouncedIagoPortCapabilities();
  previousPort?.removeEventListener('message', handlePortMessage);
  if (previousPort) closePort(previousPort);

  nextPort.addEventListener('message', handlePortMessage);
  nextPort.start();

  if (isReconnect) resetControlViewToHost();
}

function handlePortMessage(event: MessageEvent<IagoPortEvent>) {
  handlePortEvent(event.data);
}

export function initIagoPort() {
  if (didInit) return;
  didInit = true;

  window.addEventListener('message', (event: MessageEvent) => {
    if (!isIagoPortWindowMessage(event.data)) return;
    const nextPort = event.ports[0];
    const epoch = event.data.epoch;
    if (!nextPort || typeof epoch !== 'number') return;
    if (epoch <= currentEpoch) {
      closePort(nextPort);
      return;
    }
    adoptPort(nextPort, epoch);
  });

  void sendIpc('requestIagoPort');
}

export function sendIagoIngest(...[peerId, event, options]: Parameters<IagoPortRequests['ingestIagoEvent']>) {
  if (!port || !isReady) {
    if (!didLogDroppedIngest) {
      didLogDroppedIngest = true;
      console.warn('[iago-port] dropping ingest before Iago port is ready');
    }
    return;
  }

  port.postMessage({ type: 'ingest', peerId, event, options });
}

type GatedIagoPortRequest = Exclude<IagoPortRequest, { type: 'ingest' }>;

// Old shells report unknown request types as errors — never send what the
// shell didn't announce. Silent: a trailing shell is an expected state during
// updater rollout, not an error.
function postGatedIagoPortRequest(request: GatedIagoPortRequest) {
  if (!port || !isReady) return false;
  if (!hasIagoPortCapability(request.type)) return false;
  port.postMessage(request);
  return true;
}

export function sendIagoInvalidateInControlPeer(peerId: string) {
  if (!port || !isReady) {
    if (!didLogDroppedInvalidate) {
      didLogDroppedInvalidate = true;
      console.warn('[iago-port] dropping invalidate before Iago port is ready');
    }
    return;
  }

  postGatedIagoPortRequest({ type: 'invalidate-in-control-peer', peerId });
}
